import { useRef, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';

import StripeElements from '~/components/StripeElements';

import BillingAddressForm from '../BillingAddressForm';
import PaymentForm from '../PaymentForm';
import PaymentStatus from '../PaymentStatus';

import { CHECKOUT_STEPS_LIST, CHECKOUT_STEP } from './CheckoutWizard.constants';
import { CheckoutWizardProvider } from './CheckoutWizard.context';
import { useCheckoutNavigation, useCountryCode } from './CheckoutWizard.hooks';
import * as S from './CheckoutWizard.styled';
import { OrderSummary } from './OrderSummary';

export const CheckoutWizard = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'checkoutWizard' });

  const { goNext, currentStepId } = useCheckoutNavigation();

  const paymentError = useRef<string | null>(null);
  const countryCode = useCountryCode();

  useLayoutEffect(() => {
    paymentError.current = null;
  }, [currentStepId]);

  const handlePaymentError = (error: string) => {
    paymentError.current = error;
    goNext();
  };

  return (
    <CheckoutWizardProvider value={countryCode}>
      <S.Container data-testid="CheckoutWizard">
        <S.Stepper activeStep={currentStepId - 1}>
          {CHECKOUT_STEPS_LIST.map((step) => (
            <S.Step key={step.label}>
              <S.StepLabel>{step.label}</S.StepLabel>
            </S.Step>
          ))}
        </S.Stepper>
        {currentStepId === CHECKOUT_STEP.BILLING_ADDRESS.id && (
          <S.StepDetailsSection>
            <S.StepDetailsContent>
              <S.FormWrapper>
                <S.Title>{t('billingAddress.title', 'Billing details')}</S.Title>
                <S.Description>
                  {t('billingAddress.description', 'Please verify your billing address')}
                </S.Description>
                <BillingAddressForm checkoutInProgress />
              </S.FormWrapper>
            </S.StepDetailsContent>
            <OrderSummary />
          </S.StepDetailsSection>
        )}
        {currentStepId === CHECKOUT_STEP.PAYMENT.id && (
          <S.StepDetailsSection>
            <S.StepDetailsContent>
              <S.FormWrapper>
                <S.Title>{t('payment.title', 'Payment method')}</S.Title>
                <S.Description>
                  {t('payment.description', 'Choose how you would like to pay')}
                </S.Description>
                <StripeElements>
                  <PaymentForm
                    checkoutInProgress
                    // @ts-expect-error missing types
                    onError={handlePaymentError}
                  />
                </StripeElements>
              </S.FormWrapper>
            </S.StepDetailsContent>
            <OrderSummary />
          </S.StepDetailsSection>
        )}
        {currentStepId === CHECKOUT_STEP.CONFIRMATION.id && (
          <S.StepDetailsSection>
            <S.StepDetailsContent>
              <PaymentStatus error={paymentError.current} />
            </S.StepDetailsContent>
          </S.StepDetailsSection>
        )}
      </S.Container>
    </CheckoutWizardProvider>
  );
};
